import { useRef, useEffect, useState } from 'react';

export interface DivisionOption {
  value: string;
  label: string;
}

interface DivisionSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DivisionOption[];
  placeholder: string;
  disabled?: boolean;
  id?: string;
}

function DivisionSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  id,
}: DivisionSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayLabel = value
    ? options.find((o) => o.value === value)?.label ?? placeholder
    : placeholder;
  const isPlaceholder = !value;

  return (
    <div ref={ref} className="division-select" id={id}>
      <button
        type="button"
        className={`division-select-trigger ${isPlaceholder ? 'placeholder' : ''} ${open ? 'open' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={placeholder}
      >
        <span className="division-select-value">{displayLabel}</span>
        <svg className="division-select-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path fill="currentColor" d="M6 8L1 3h10z" />
        </svg>
      </button>
      {open && (
        <ul
          className="division-select-dropdown"
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          <li
            role="option"
            className="division-select-option"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            {placeholder}
          </li>
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
              className="division-select-option"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DivisionSelect;
